// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Core from './configuration.core';

export enum QuestionValidationStates {
    Valid,
    SoftInvalid,
    HardInvalid
}

export enum ValidationMessageStates {
    Info,
    SoftInvalid,
    HardInvalid
}

export enum ConflictStates {
    Valid,
    SoftInvalid,
    HardInvalid
}

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isLinkedLayoutModel(source: any): source is ILinkedLayout {
    // Method Implementation: simple
    return source.modelName === 'LinkedLayout';
}

export function isHistoricalStateModel(source: any): source is IHistoricalState {
    // Method Implementation: simple
    return source.modelName === 'HistoricalState';
}

export function isGroupModel(source: any): source is IGroup {
    // Method Implementation: simple
    return source.modelName === 'Group';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export function isStringQuestionModel(source: any): source is IStringQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringQuestion';
}

export function isNumericQuestionModel(source: any): source is INumericQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericQuestion';
}

export function isBooleanQuestionModel(source: any): source is IBooleanQuestion {
    // Method Implementation: simple
    return source.modelName === 'BooleanQuestion';
}

export function isStringEnumerableQuestionModel(source: any): source is IStringEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableQuestion';
}

export function isNumericEnumerableQuestionModel(source: any): source is INumericEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableQuestion';
}

export function isMultiStringQuestionModel(source: any): source is IMultiStringQuestion {
    // Method Implementation: simple
    return source.modelName === 'MultiStringQuestion';
}

export function isMultiNumericQuestionModel(source: any): source is IMultiNumericQuestion {
    // Method Implementation: simple
    return source.modelName === 'MultiNumericQuestion';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isStringAnswerModel(source: any): source is IStringAnswer {
    // Method Implementation: simple
    return source.modelName === 'StringAnswer';
}

export function isNumericAnswerModel(source: any): source is INumericAnswer {
    // Method Implementation: simple
    return source.modelName === 'NumericAnswer';
}

export function isValidationMessageModel(source: any): source is IValidationMessage {
    // Method Implementation: simple
    return source.modelName === 'ValidationMessage';
}

export function isConflictScenarioModel(source: any): source is IConflictScenario {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenario';
}

export function isRecommendedValueModel(source: any): source is IRecommendedValue {
    // Method Implementation: simple
    return source.modelName === 'RecommendedValue';
}

export interface EntityZodSchemaShape {
    entityId?: string;

    groups?: GroupZodSchemaShape[];

    validationMessages?: ValidationMessageZodSchemaShape[];

    conflict?: ConflictScenarioZodSchemaShape;

    state?: 'Valid' | 'Invalid' | 'Incomplete';

    historicalStates?: HistoricalStateZodSchemaShape[];

    layouts?: LinkedLayoutZodSchemaShape[];
}

export interface LinkedLayoutZodSchemaShape {
    name?: string;

    displayName?: string;

    layout?: EntityZodSchemaShape;
}

export interface HistoricalStateZodSchemaShape {
    description?: string;
}

export interface GroupZodSchemaShape {
    name?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    questions?: QuestionZodSchemaShape[];

    groups?: GroupZodSchemaShape[];

    validationMessages?: ValidationMessageZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;
}

export interface StringQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: string;
}

export interface NumericQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: number;

    precision?: number;

    min?: number;

    max?: number;
}

export interface BooleanQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: boolean;
}

export interface StringEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: string;

    allowCustomValue?: boolean;

    answers?: StringAnswerZodSchemaShape[];
}

export interface NumericEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: number;

    allowCustomValue?: boolean;

    answers?: NumericAnswerZodSchemaShape[];

    precision?: number;

    min?: number;

    max?: number;
}

export interface MultiStringQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    values?: string[];
}

export interface MultiNumericQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    valueSource?: string;

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    values?: number[];

    precision?: number;

    min?: number;

    max?: number;
}

export interface AnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';
}

export interface StringAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: string;
}

export interface NumericAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: number;
}

export interface ValidationMessageZodSchemaShape {
    message?: string;

    type?: 'Info' | 'SoftInvalid' | 'HardInvalid';

    transient?: boolean;
}

export interface ConflictScenarioZodSchemaShape {
    state?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    messages?: ValidationMessageZodSchemaShape[];
}

export interface RecommendedValueZodSchemaShape {
    source?: string;

    displayValue?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    groups: Zod.array(GroupZodSchema).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    conflict: ConflictScenarioZodSchema.optional(),
    state: Zod.enum([ 'Valid', 'Invalid', 'Incomplete' ]).optional(),
    historicalStates: Zod.array(HistoricalStateZodSchema).optional(),
    layouts: Zod.array(LinkedLayoutZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedLayoutZodSchema: Zod.ZodSchema<LinkedLayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    layout: EntityZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const HistoricalStateZodSchema: Zod.ZodSchema<HistoricalStateZodSchemaShape> = Zod.lazy(() => Zod.object({
    description: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const GroupZodSchema: Zod.ZodSchema<GroupZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    questions: Zod.array(QuestionZodSchema).optional(),
    groups: Zod.array(GroupZodSchema).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringQuestionZodSchema: Zod.ZodSchema<StringQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericQuestionZodSchema: Zod.ZodSchema<NumericQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.number().optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanQuestionZodSchema: Zod.ZodSchema<BooleanQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableQuestionZodSchema: Zod.ZodSchema<StringEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.string().optional(),
    allowCustomValue: Zod.boolean().optional(),
    answers: Zod.array(StringAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableQuestionZodSchema: Zod.ZodSchema<NumericEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.number().optional(),
    allowCustomValue: Zod.boolean().optional(),
    answers: Zod.array(NumericAnswerZodSchema).optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiStringQuestionZodSchema: Zod.ZodSchema<MultiStringQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    values: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiNumericQuestionZodSchema: Zod.ZodSchema<MultiNumericQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    valueSource: Zod.string().optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    values: Zod.array(Zod.number()).optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringAnswerZodSchema: Zod.ZodSchema<StringAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericAnswerZodSchema: Zod.ZodSchema<NumericAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValidationMessageZodSchema: Zod.ZodSchema<ValidationMessageZodSchemaShape> = Zod.lazy(() => Zod.object({
    message: Zod.string().optional(),
    type: Zod.enum([ 'Info', 'SoftInvalid', 'HardInvalid' ]).optional(),
    transient: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioZodSchema: Zod.ZodSchema<ConflictScenarioZodSchemaShape> = Zod.lazy(() => Zod.object({
    state: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    messages: Zod.array(ValidationMessageZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const RecommendedValueZodSchema: Zod.ZodSchema<RecommendedValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    displayValue: Zod.string().optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly groups: Group[];

    readonly validationMessages: ValidationMessage[];

    readonly conflict: ConflictScenario;

    readonly state: Core.EntityStates;

    readonly historicalStates: HistoricalState[];

    readonly layouts: LinkedLayout[];

    readonly isEntityIdDefined: boolean;

    readonly isGroupsDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isConflictDefined: boolean;

    readonly isStateDefined: boolean;

    readonly isHistoricalStatesDefined: boolean;

    readonly isLayoutsDefined: boolean;
}

export interface ILinkedLayout extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly layout: Entity;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isLayoutDefined: boolean;
}

export interface IHistoricalState extends Wellspring.IModel {
    readonly modelName: string;

    readonly description: string;

    readonly isDescriptionDefined: boolean;
}

export interface IGroup extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly helpText: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly questions: AQuestion[];

    readonly groups: Group[];

    readonly validationMessages: ValidationMessage[];

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isHelpTextDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isQuestionsDefined: boolean;

    readonly isGroupsDefined: boolean;

    readonly isValidationMessagesDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly displayName: string;

    readonly helpText: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly required: boolean;

    readonly validationState: QuestionValidationStates;

    readonly validationMessages: ValidationMessage[];

    readonly valueSource: string;

    readonly displayValue: string;

    readonly assignmentState: Core.AssignmentStates;

    readonly recommendedDisplayValue: string;

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isHelpTextDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isValidationStateDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isValueSourceDefined: boolean;

    readonly isDisplayValueDefined: boolean;

    readonly isAssignmentStateDefined: boolean;

    readonly isRecommendedDisplayValueDefined: boolean;
}

export interface IStringQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: string;

    readonly isValueDefined: boolean;
}

export interface INumericQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: number;

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValueDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IBooleanQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: boolean;

    readonly isValueDefined: boolean;
}

export interface IStringEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: string;

    readonly allowCustomValue: boolean;

    readonly answers: StringAnswer[];

    readonly isValueDefined: boolean;

    readonly isAllowCustomValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface INumericEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: number;

    readonly allowCustomValue: boolean;

    readonly answers: NumericAnswer[];

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValueDefined: boolean;

    readonly isAllowCustomValueDefined: boolean;

    readonly isAnswersDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IMultiStringQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly values?: string[];

    readonly isValuesDefined: boolean;
}

export interface IMultiNumericQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly values?: number[];

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValuesDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly assignmentState: Core.OptionStates;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isAssignmentStateDefined: boolean;
}

export interface IStringAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IValidationMessage extends Wellspring.IModel {
    readonly modelName: string;

    readonly message: string;

    readonly type: ValidationMessageStates;

    readonly transient: boolean;

    readonly isMessageDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isTransientDefined: boolean;
}

export interface IConflictScenario extends Wellspring.IModel {
    readonly modelName: string;

    readonly state: ConflictStates;

    readonly messages: ValidationMessage[];

    readonly isStateDefined: boolean;

    readonly isMessagesDefined: boolean;
}

export interface IRecommendedValue extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly displayValue: string;

    readonly isSourceDefined: boolean;

    readonly isDisplayValueDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'groups', 'validationMessages', 'conflict', 'state', 'historicalStates', 'layouts' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _entityId: string;

    private _groups: Group[];

    private _validationMessages: ValidationMessage[];

    private _conflict: ConflictScenario;

    private _state: Core.EntityStates;

    private _historicalStates: HistoricalState[];

    private _layouts: LinkedLayout[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get conflict(): ConflictScenario {
        // Method Implementation: simple
        return this._conflict;
    }

    public get state(): Core.EntityStates {
        // Method Implementation: simple
        return this._state;
    }

    public get historicalStates(): HistoricalState[] {
        // Method Implementation: simple
        return this._historicalStates;
    }

    public get layouts(): LinkedLayout[] {
        // Method Implementation: simple
        return this._layouts;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set conflict(value: ConflictScenario) {
        // Method Implementation: simple
        this._conflict = value;
        this._isConflictDefined = true;
    }

    public set state(value: Core.EntityStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    public set historicalStates(value: HistoricalState[]) {
        // Method Implementation: simple
        this._historicalStates = value;
        this._isHistoricalStatesDefined = true;
    }

    public set layouts(value: LinkedLayout[]) {
        // Method Implementation: simple
        this._layouts = value;
        this._isLayoutsDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isGroupsDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isConflictDefined: boolean;

    private _isStateDefined: boolean;

    private _isHistoricalStatesDefined: boolean;

    private _isLayoutsDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isConflictDefined(): boolean {
        // Method Implementation: simple
        return this._isConflictDefined;
    }

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public get isHistoricalStatesDefined(): boolean {
        // Method Implementation: simple
        return this._isHistoricalStatesDefined;
    }

    public get isLayoutsDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutsDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'entityId' | 'groups' | 'validationMessages' | 'conflict' | 'state' | 'historicalStates' | 'layouts'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'entityId' | 'groups' | 'validationMessages' | 'conflict' | 'state' | 'historicalStates' | 'layouts'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isConflictDefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.isHistoricalStatesDefined) {
                    this._historicalStates = source.historicalStates;
                    this._isHistoricalStatesDefined = true;
                }
                else {
                    this._historicalStates = [];
                    this._isHistoricalStatesDefined = true;
                }
                
                if (source.isLayoutsDefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = [];
                    this._isLayoutsDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.conflict !== undefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.historicalStates !== undefined) {
                    this._historicalStates = source.historicalStates;
                    this._isHistoricalStatesDefined = true;
                }
                else {
                    this._historicalStates = [];
                    this._isHistoricalStatesDefined = true;
                }
                
                if (source.layouts !== undefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = [];
                    this._isLayoutsDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._groups = [];
            this._isGroupsDefined = true;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._conflict = new ConflictScenario();
            this._isConflictDefined = true;
        
            this._state = Core.EntityStates.Valid;
            this._isStateDefined = false;
        
            this._historicalStates = [];
            this._isHistoricalStatesDefined = true;
        
            this._layouts = [];
            this._isLayoutsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isConflictDefined && this.conflict !== undefined)
            definedProperties.push('conflict');
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined)
            definedProperties.push('historicalStates');
        if (this.isLayoutsDefined && this.layouts !== undefined)
            definedProperties.push('layouts');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'groups':
                return this.groups;
            case 'validationMessages':
                return this.validationMessages;
            case 'conflict':
                return this.conflict;
            case 'state':
                return this.state;
            case 'historicalStates':
                return this.historicalStates;
            case 'layouts':
                return this.layouts;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isConflictDefined && this.conflict !== undefined) {
            if (this.conflict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.conflict.getModelName(), this.conflict.getDefinedPropertyNames().length));
                this.conflict.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'null'));
            }
        }
        
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
        
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined) {
            if (this.historicalStates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('historicalStates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.historicalStates.length));
                for (let i = 0; i < this.historicalStates.length; i++) {
                    const item = this.historicalStates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('historicalStates', 'null'));
            }
        }
        
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            if (this.layouts !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.layouts.length));
                for (let i = 0; i < this.layouts.length; i++) {
                    const item = this.layouts[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'conflict':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConflictScenario.modelName) {
                        this.conflict = new ConflictScenario();
                        this.conflict.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenario.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'conflict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'conflict'`);
                }
        
                return true;
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            case 'historicalStates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.historicalStates = [];
                
                    let item: HistoricalState = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === HistoricalState.modelName) {
                                item = new HistoricalState();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${HistoricalState.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'historicalStates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'historicalStates'`);
                        }
                
                        this.historicalStates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'historicalStates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'historicalStates'`);
                }
        
                return true;
            case 'layouts':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.layouts = [];
                
                    let item: LinkedLayout = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LinkedLayout.modelName) {
                                item = new LinkedLayout();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LinkedLayout.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'layouts' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'layouts'`);
                        }
                
                        this.layouts.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layouts' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layouts'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // conflict
        if (this.isConflictDefined && this.conflict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.conflict === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.conflict.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // historicalStates
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.historicalStates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.historicalStates.length);
            
                for (const item of this.historicalStates) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layouts
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.layouts === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.layouts.length);
            
                for (const item of this.layouts) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // conflict
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.conflict = new ConflictScenario();
                    this.conflict.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
        
        // historicalStates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: HistoricalState;
            
                    this.historicalStates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new HistoricalState();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.historicalStates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layouts
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: LinkedLayout;
            
                    this.layouts = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new LinkedLayout();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.layouts.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            entityId: schema.entityId,
            groups: schema.groups?.map(i => Group.parseZodSchema(i)),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            conflict: schema.conflict && ConflictScenario.parseZodSchema(schema.conflict),
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Valid':
                    return Core.EntityStates.Valid;
                case 'Invalid':
                    return Core.EntityStates.Invalid;
                case 'Incomplete':
                    return Core.EntityStates.Incomplete;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            historicalStates: schema.historicalStates?.map(i => HistoricalState.parseZodSchema(i)),
            layouts: schema.layouts?.map(i => LinkedLayout.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
            conflict: this.isConflictDefined ? (this.conflict) : undefined,
            state: this.isStateDefined ? (Core.EntityStates[this.state]) : undefined,
            historicalStates: this.isHistoricalStatesDefined ? (this.historicalStates?.map(item => item)) : undefined,
            layouts: this.isLayoutsDefined ? (this.layouts?.map(item => item)) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            conflict: this.isConflictDefined ? this.conflict.clone() : undefined,
            state: this.isStateDefined ? this.state : undefined,
            historicalStates: this.isHistoricalStatesDefined ? this.historicalStates?.map(i => i.clone()) : undefined,
            layouts: this.isLayoutsDefined ? this.layouts?.map(i => i.clone()) : undefined
        });
    }
}

export class LinkedLayout implements ILinkedLayout {
    public static readonly modelName: string = 'LinkedLayout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'layout' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LinkedLayout.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _layout: Entity;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get layout(): Entity {
        // Method Implementation: simple
        return this._layout;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set layout(value: Entity) {
        // Method Implementation: simple
        this._layout = value;
        this._isLayoutDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isLayoutDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutDefined;
    }

    public constructor();
    public constructor(source: ILinkedLayout);
    public constructor(source: Partial<Pick<LinkedLayout, 'name' | 'displayName' | 'layout'>>);
    public constructor(source?: ILinkedLayout | Partial<Pick<LinkedLayout, 'name' | 'displayName' | 'layout'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedLayoutModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isLayoutDefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new Entity();
                    this._isLayoutDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.layout !== undefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new Entity();
                    this._isLayoutDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._layout = new Entity();
            this._isLayoutDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LinkedLayout.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isLayoutDefined && this.layout !== undefined)
            definedProperties.push('layout');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'layout':
                return this.layout;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isLayoutDefined && this.layout !== undefined) {
            if (this.layout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.layout.getModelName(), this.layout.getDefinedPropertyNames().length));
                this.layout.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'layout':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Entity.modelName) {
                        this.layout = new Entity();
                        this.layout.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layout' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layout'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layout
        if (this.isLayoutDefined && this.layout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.layout === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.layout.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layout
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.layout = new Entity();
                    this.layout.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LinkedLayout {
        // Method Implementation: simple
        const schema = LinkedLayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedLayoutZodSchemaShape): LinkedLayout {
        // Method Implementation: parseZodSchema
        return new LinkedLayout({
            name: schema.name,
            displayName: schema.displayName,
            layout: schema.layout && Entity.parseZodSchema(schema.layout)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            layout: this.isLayoutDefined ? (this.layout) : undefined,
        };
    }

    public clone(): LinkedLayout {
        // Method Implementation: clone
        return new LinkedLayout({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            layout: this.isLayoutDefined ? this.layout.clone() : undefined
        });
    }
}

export class HistoricalState implements IHistoricalState {
    public static readonly modelName: string = 'HistoricalState';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'description' ];

    public get modelName(): string {
        // Method Implementation: simple
        return HistoricalState.modelName;
    }

    private _description: string;

    public get description(): string {
        // Method Implementation: simple
        return this._description;
    }

    public set description(value: string) {
        // Method Implementation: simple
        this._description = value;
        this._isDescriptionDefined = true;
    }

    private _isDescriptionDefined: boolean;

    public get isDescriptionDefined(): boolean {
        // Method Implementation: simple
        return this._isDescriptionDefined;
    }

    public constructor();
    public constructor(source: IHistoricalState);
    public constructor(source: Partial<Pick<HistoricalState, 'description'>>);
    public constructor(source?: IHistoricalState | Partial<Pick<HistoricalState, 'description'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isHistoricalStateModel(source)) {
                if (source.isDescriptionDefined) {
                    this._description = source.description;
                    this._isDescriptionDefined = true;
                }
                else {
                    this._description = '';
                    this._isDescriptionDefined = false;
                }
            }
            else {
                if (source.description !== undefined) {
                    this._description = source.description;
                    this._isDescriptionDefined = true;
                }
                else {
                    this._description = '';
                    this._isDescriptionDefined = false;
                }
            }
        }
        else {
            this._description = '';
            this._isDescriptionDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return HistoricalState.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isDescriptionDefined && this.description !== undefined)
            definedProperties.push('description');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'description':
                return this.description;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isDescriptionDefined && this.description !== undefined) {
            if (this.description !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('description', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.description);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('description', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'description':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.description = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'description' cannot be null`);
                    this.description = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'description'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // description
        if (this.isDescriptionDefined && this.description !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.description === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.description);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // description
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.description = <any>null;
                    break;
                case 1:
                    this.description = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): HistoricalState {
        // Method Implementation: simple
        const schema = HistoricalStateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: HistoricalStateZodSchemaShape): HistoricalState {
        // Method Implementation: parseZodSchema
        return new HistoricalState({
            description: schema.description
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            description: this.isDescriptionDefined ? ((this.description === null ? undefined : this.description)) : undefined,
        };
    }

    public clone(): HistoricalState {
        // Method Implementation: clone
        return new HistoricalState({
            description: this.isDescriptionDefined ? this.description : undefined
        });
    }
}

export class Group implements IGroup {
    public static readonly modelName: string = 'Group';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'helpText', 'enabled', 'visible', 'questions', 'groups', 'validationMessages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Group.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _helpText: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _questions: AQuestion[];

    private _groups: Group[];

    private _validationMessages: ValidationMessage[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get helpText(): string {
        // Method Implementation: simple
        return this._helpText;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get questions(): AQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set helpText(value: string) {
        // Method Implementation: simple
        this._helpText = value;
        this._isHelpTextDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set questions(value: AQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isHelpTextDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isQuestionsDefined: boolean;

    private _isGroupsDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isHelpTextDefined(): boolean {
        // Method Implementation: simple
        return this._isHelpTextDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public constructor();
    public constructor(source: IGroup);
    public constructor(source: Partial<Pick<Group, 'name' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'questions' | 'groups' | 'validationMessages'>>);
    public constructor(source?: IGroup | Partial<Pick<Group, 'name' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'questions' | 'groups' | 'validationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGroupModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isHelpTextDefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.helpText !== undefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._helpText = '';
            this._isHelpTextDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        
            this._groups = [];
            this._isGroupsDefined = true;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Group.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isHelpTextDefined && this.helpText !== undefined)
            definedProperties.push('helpText');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'helpText':
                return this.helpText;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'questions':
                return this.questions;
            case 'groups':
                return this.groups;
            case 'validationMessages':
                return this.validationMessages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            if (this.helpText !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.helpText);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
        
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'helpText':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.helpText = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'helpText' cannot be null`);
                    this.helpText = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'helpText'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: AQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // helpText
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.helpText === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.helpText);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringQuestion)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericQuestion)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanQuestion)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableQuestion)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableQuestion)
                            writer.writeShortValue(4);
                        else if (item instanceof MultiStringQuestion)
                            writer.writeShortValue(5);
                        else if (item instanceof MultiNumericQuestion)
                            writer.writeShortValue(6);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // helpText
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.helpText = <any>null;
                    break;
                case 1:
                    this.helpText = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringQuestion();
                                        break;
                                    case 1:
                                        item = new NumericQuestion();
                                        break;
                                    case 2:
                                        item = new BooleanQuestion();
                                        break;
                                    case 3:
                                        item = new StringEnumerableQuestion();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableQuestion();
                                        break;
                                    case 5:
                                        item = new MultiStringQuestion();
                                        break;
                                    case 6:
                                        item = new MultiNumericQuestion();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Group {
        // Method Implementation: simple
        const schema = GroupZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GroupZodSchemaShape): Group {
        // Method Implementation: parseZodSchema
        return new Group({
            name: schema.name,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            questions: schema.questions?.map(i => AQuestion.parseZodSchema(i)),
            groups: schema.groups?.map(i => Group.parseZodSchema(i)),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            helpText: this.isHelpTextDefined ? ((this.helpText === null ? undefined : this.helpText)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
        };
    }

    public clone(): Group {
        // Method Implementation: clone
        return new Group({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AQuestion implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source', 'displayName', 'helpText', 'enabled', 'visible', 'required', 'validationState', 'validationMessages', 'valueSource', 'displayValue', 'assignmentState', 'recommendedDisplayValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AQuestion.modelName;
    }

    private _name: string;

    private _source: string;

    private _displayName: string;

    private _helpText: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _required: boolean;

    private _validationState: QuestionValidationStates;

    private _validationMessages: ValidationMessage[];

    private _valueSource: string;

    private _displayValue: string;

    private _assignmentState: Core.AssignmentStates;

    private _recommendedDisplayValue: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get helpText(): string {
        // Method Implementation: simple
        return this._helpText;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get required(): boolean {
        // Method Implementation: simple
        return this._required;
    }

    public get validationState(): QuestionValidationStates {
        // Method Implementation: simple
        return this._validationState;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get valueSource(): string {
        // Method Implementation: simple
        return this._valueSource;
    }

    public get displayValue(): string {
        // Method Implementation: simple
        return this._displayValue;
    }

    public get assignmentState(): Core.AssignmentStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public get recommendedDisplayValue(): string {
        // Method Implementation: simple
        return this._recommendedDisplayValue;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set helpText(value: string) {
        // Method Implementation: simple
        this._helpText = value;
        this._isHelpTextDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set required(value: boolean) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set validationState(value: QuestionValidationStates) {
        // Method Implementation: simple
        this._validationState = value;
        this._isValidationStateDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set valueSource(value: string) {
        // Method Implementation: simple
        this._valueSource = value;
        this._isValueSourceDefined = true;
    }

    public set displayValue(value: string) {
        // Method Implementation: simple
        this._displayValue = value;
        this._isDisplayValueDefined = true;
    }

    public set assignmentState(value: Core.AssignmentStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    public set recommendedDisplayValue(value: string) {
        // Method Implementation: simple
        this._recommendedDisplayValue = value;
        this._isRecommendedDisplayValueDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isHelpTextDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isValidationStateDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isValueSourceDefined: boolean;

    private _isDisplayValueDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    private _isRecommendedDisplayValueDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isHelpTextDefined(): boolean {
        // Method Implementation: simple
        return this._isHelpTextDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isValidationStateDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationStateDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isValueSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isValueSourceDefined;
    }

    public get isDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayValueDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public get isRecommendedDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isRecommendedDisplayValueDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<AQuestion, 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IQuestion | Partial<Pick<AQuestion, 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isHelpTextDefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.isValidationStateDefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = QuestionValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isValueSourceDefined) {
                    this._valueSource = source.valueSource;
                    this._isValueSourceDefined = true;
                }
                else {
                    this._valueSource = '';
                    this._isValueSourceDefined = false;
                }
                
                if (source.isDisplayValueDefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.AssignmentStates.Unassigned;
                    this._isAssignmentStateDefined = false;
                }
                
                if (source.isRecommendedDisplayValueDefined) {
                    this._recommendedDisplayValue = source.recommendedDisplayValue;
                    this._isRecommendedDisplayValueDefined = true;
                }
                else {
                    this._recommendedDisplayValue = '';
                    this._isRecommendedDisplayValueDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.helpText !== undefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.validationState !== undefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = QuestionValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.valueSource !== undefined) {
                    this._valueSource = source.valueSource;
                    this._isValueSourceDefined = true;
                }
                else {
                    this._valueSource = '';
                    this._isValueSourceDefined = false;
                }
                
                if (source.displayValue !== undefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.AssignmentStates.Unassigned;
                    this._isAssignmentStateDefined = false;
                }
                
                if (source.recommendedDisplayValue !== undefined) {
                    this._recommendedDisplayValue = source.recommendedDisplayValue;
                    this._isRecommendedDisplayValueDefined = true;
                }
                else {
                    this._recommendedDisplayValue = '';
                    this._isRecommendedDisplayValueDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._helpText = '';
            this._isHelpTextDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._required = false;
            this._isRequiredDefined = false;
        
            this._validationState = QuestionValidationStates.Valid;
            this._isValidationStateDefined = false;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._valueSource = '';
            this._isValueSourceDefined = false;
        
            this._displayValue = '';
            this._isDisplayValueDefined = false;
        
            this._assignmentState = Core.AssignmentStates.Unassigned;
            this._isAssignmentStateDefined = false;
        
            this._recommendedDisplayValue = '';
            this._isRecommendedDisplayValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AQuestion.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isHelpTextDefined && this.helpText !== undefined)
            definedProperties.push('helpText');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isValidationStateDefined && this.validationState !== undefined)
            definedProperties.push('validationState');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isValueSourceDefined && this.valueSource !== undefined)
            definedProperties.push('valueSource');
        if (this.isDisplayValueDefined && this.displayValue !== undefined)
            definedProperties.push('displayValue');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined)
            definedProperties.push('recommendedDisplayValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            case 'displayName':
                return this.displayName;
            case 'helpText':
                return this.helpText;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'required':
                return this.required;
            case 'validationState':
                return this.validationState;
            case 'validationMessages':
                return this.validationMessages;
            case 'valueSource':
                return this.valueSource;
            case 'displayValue':
                return this.displayValue;
            case 'assignmentState':
                return this.assignmentState;
            case 'recommendedDisplayValue':
                return this.recommendedDisplayValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            if (this.helpText !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.helpText);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            if (this.validationState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.validationState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isValueSourceDefined && this.valueSource !== undefined) {
            if (this.valueSource !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('valueSource', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.valueSource);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('valueSource', 'null'));
            }
        }
        
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            if (this.displayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
        
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined) {
            if (this.recommendedDisplayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('recommendedDisplayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.recommendedDisplayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('recommendedDisplayValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'helpText':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.helpText = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'helpText' cannot be null`);
                    this.helpText = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'helpText'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.required = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'required' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'validationState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.validationState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'validationState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationState'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'valueSource':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.valueSource = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'valueSource' cannot be null`);
                    this.valueSource = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'valueSource'`);
                }
        
                return true;
            case 'displayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayValue' cannot be null`);
                    this.displayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayValue'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            case 'recommendedDisplayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.recommendedDisplayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'recommendedDisplayValue' cannot be null`);
                    this.recommendedDisplayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'recommendedDisplayValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // helpText
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.helpText === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.helpText);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.required);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationState
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.validationState);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // valueSource
        if (this.isValueSourceDefined && this.valueSource !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.valueSource === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.valueSource);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayValue
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // recommendedDisplayValue
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.recommendedDisplayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.recommendedDisplayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // helpText
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.helpText = <any>null;
                    break;
                case 1:
                    this.helpText = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.required = reader.readBooleanValue();
        }
        
        // validationState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.validationState = reader.readEnumValue();
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // valueSource
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.valueSource = <any>null;
                    break;
                case 1:
                    this.valueSource = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayValue = <any>null;
                    break;
                case 1:
                    this.displayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
        
        // recommendedDisplayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.recommendedDisplayValue = <any>null;
                    break;
                case 1:
                    this.recommendedDisplayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AQuestion {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): AQuestion {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.questionType)
        {
            case 'stringQuestion':
            case 'string':
                return StringQuestion.parse(schema);
            case 'numericQuestion':
            case 'numeric':
                return NumericQuestion.parse(schema);
            case 'booleanQuestion':
            case 'boolean':
                return BooleanQuestion.parse(schema);
            case 'stringEnumerableQuestion':
            case 'stringEnumerable':
                return StringEnumerableQuestion.parse(schema);
            case 'numericEnumerableQuestion':
            case 'numericEnumerable':
                return NumericEnumerableQuestion.parse(schema);
            case 'multiStringQuestion':
            case 'multiString':
                return MultiStringQuestion.parse(schema);
            case 'multiNumericQuestion':
            case 'multiNumeric':
                return MultiNumericQuestion.parse(schema);
            default:
                throw new Error(`questionType of ${ schema.questionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            helpText: this.isHelpTextDefined ? ((this.helpText === null ? undefined : this.helpText)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            required: this.isRequiredDefined ? (this.required) : undefined,
            validationState: this.isValidationStateDefined ? (QuestionValidationStates[this.validationState]) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
            valueSource: this.isValueSourceDefined ? ((this.valueSource === null ? undefined : this.valueSource)) : undefined,
            displayValue: this.isDisplayValueDefined ? ((this.displayValue === null ? undefined : this.displayValue)) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.AssignmentStates[this.assignmentState]) : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? ((this.recommendedDisplayValue === null ? undefined : this.recommendedDisplayValue)) : undefined,
        };
    }

    public abstract clone(): AQuestion;
}

export class StringQuestion extends AQuestion implements IStringQuestion {
    public static readonly modelName: string = 'StringQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringQuestion.modelName;
    }

    private _value?: string;

    public get value(): string | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringQuestion);
    public constructor(source: Partial<Pick<StringQuestion, 'value' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IStringQuestion | Partial<Pick<StringQuestion, 'value' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringQuestion {
        // Method Implementation: simple
        const schema = StringQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringQuestionZodSchemaShape): StringQuestion {
        // Method Implementation: parseZodSchema
        return new StringQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringQuestion {
        // Method Implementation: clone
        return new StringQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericQuestion extends AQuestion implements INumericQuestion {
    public static readonly modelName: string = 'NumericQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericQuestion.modelName;
    }

    private _value?: number;

    private _precision: number;

    private _min: number;

    private _max: number;

    public get value(): number | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set value(value: number | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValueDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericQuestion);
    public constructor(source: Partial<Pick<NumericQuestion, 'value' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: INumericQuestion | Partial<Pick<NumericQuestion, 'value' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericQuestion {
        // Method Implementation: simple
        const schema = NumericQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericQuestionZodSchemaShape): NumericQuestion {
        // Method Implementation: parseZodSchema
        return new NumericQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): NumericQuestion {
        // Method Implementation: clone
        return new NumericQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class BooleanQuestion extends AQuestion implements IBooleanQuestion {
    public static readonly modelName: string = 'BooleanQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanQuestion.modelName;
    }

    private _value?: boolean;

    public get value(): boolean | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanQuestion);
    public constructor(source: Partial<Pick<BooleanQuestion, 'value' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IBooleanQuestion | Partial<Pick<BooleanQuestion, 'value' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanQuestion {
        // Method Implementation: simple
        const schema = BooleanQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanQuestionZodSchemaShape): BooleanQuestion {
        // Method Implementation: parseZodSchema
        return new BooleanQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanQuestion {
        // Method Implementation: clone
        return new BooleanQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class StringEnumerableQuestion extends AQuestion implements IStringEnumerableQuestion {
    public static readonly modelName: string = 'StringEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'allowCustomValue', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableQuestion.modelName;
    }

    private _value?: string;

    private _allowCustomValue: boolean;

    private _answers: StringAnswer[];

    public get value(): string | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get allowCustomValue(): boolean {
        // Method Implementation: simple
        return this._allowCustomValue;
    }

    public get answers(): StringAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: string | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set allowCustomValue(value: boolean) {
        // Method Implementation: simple
        this._allowCustomValue = value;
        this._isAllowCustomValueDefined = true;
    }

    public set answers(value: StringAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAllowCustomValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAllowCustomValueDefined(): boolean {
        // Method Implementation: simple
        return this._isAllowCustomValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableQuestion);
    public constructor(source: Partial<Pick<StringEnumerableQuestion, 'value' | 'allowCustomValue' | 'answers' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IStringEnumerableQuestion | Partial<Pick<StringEnumerableQuestion, 'value' | 'allowCustomValue' | 'answers' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isAllowCustomValueDefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.allowCustomValue !== undefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        
            this._allowCustomValue = false;
            this._isAllowCustomValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined)
            definedProperties.push('allowCustomValue');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'allowCustomValue':
                return this.allowCustomValue;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            if (this.allowCustomValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.allowCustomValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'allowCustomValue':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.allowCustomValue = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'allowCustomValue' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'allowCustomValue'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: StringAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringAnswer.modelName) {
                                item = new StringAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // allowCustomValue
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.allowCustomValue);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // allowCustomValue
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.allowCustomValue = reader.readBooleanValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableQuestion {
        // Method Implementation: simple
        const schema = StringEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableQuestionZodSchemaShape): StringEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new StringEnumerableQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            allowCustomValue: schema.allowCustomValue,
            answers: schema.answers?.map(i => StringAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'stringEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? (this.allowCustomValue) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableQuestion {
        // Method Implementation: clone
        return new StringEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? this.allowCustomValue : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableQuestion extends AQuestion implements INumericEnumerableQuestion {
    public static readonly modelName: string = 'NumericEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'allowCustomValue', 'answers', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableQuestion.modelName;
    }

    private _value?: number;

    private _allowCustomValue: boolean;

    private _answers: NumericAnswer[];

    private _precision: number;

    private _min: number;

    private _max: number;

    public get value(): number | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get allowCustomValue(): boolean {
        // Method Implementation: simple
        return this._allowCustomValue;
    }

    public get answers(): NumericAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set value(value: number | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set allowCustomValue(value: boolean) {
        // Method Implementation: simple
        this._allowCustomValue = value;
        this._isAllowCustomValueDefined = true;
    }

    public set answers(value: NumericAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAllowCustomValueDefined: boolean;

    private _isAnswersDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAllowCustomValueDefined(): boolean {
        // Method Implementation: simple
        return this._isAllowCustomValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableQuestion);
    public constructor(source: Partial<Pick<NumericEnumerableQuestion, 'value' | 'allowCustomValue' | 'answers' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: INumericEnumerableQuestion | Partial<Pick<NumericEnumerableQuestion, 'value' | 'allowCustomValue' | 'answers' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isAllowCustomValueDefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.allowCustomValue !== undefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._allowCustomValue = false;
            this._isAllowCustomValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined)
            definedProperties.push('allowCustomValue');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'allowCustomValue':
                return this.allowCustomValue;
            case 'answers':
                return this.answers;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            if (this.allowCustomValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.allowCustomValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'allowCustomValue':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.allowCustomValue = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'allowCustomValue' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'allowCustomValue'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: NumericAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericAnswer.modelName) {
                                item = new NumericAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // allowCustomValue
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.allowCustomValue);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // allowCustomValue
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.allowCustomValue = reader.readBooleanValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericEnumerableQuestion {
        // Method Implementation: simple
        const schema = NumericEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableQuestionZodSchemaShape): NumericEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            allowCustomValue: schema.allowCustomValue,
            answers: schema.answers?.map(i => NumericAnswer.parseZodSchema(i)),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numericEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? (this.allowCustomValue) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): NumericEnumerableQuestion {
        // Method Implementation: clone
        return new NumericEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? this.allowCustomValue : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class MultiStringQuestion extends AQuestion implements IMultiStringQuestion {
    public static readonly modelName: string = 'MultiStringQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiStringQuestion.modelName;
    }

    private _values?: string[];

    public get values(): string[] | undefined {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: string[] | undefined) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiStringQuestion);
    public constructor(source: Partial<Pick<MultiStringQuestion, 'values' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IMultiStringQuestion | Partial<Pick<MultiStringQuestion, 'values' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiStringQuestionModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiStringQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiStringQuestion {
        // Method Implementation: simple
        const schema = MultiStringQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiStringQuestionZodSchemaShape): MultiStringQuestion {
        // Method Implementation: parseZodSchema
        return new MultiStringQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'multiString',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): MultiStringQuestion {
        // Method Implementation: clone
        return new MultiStringQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export class MultiNumericQuestion extends AQuestion implements IMultiNumericQuestion {
    public static readonly modelName: string = 'MultiNumericQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiNumericQuestion.modelName;
    }

    private _values?: number[];

    private _precision: number;

    private _min: number;

    private _max: number;

    public get values(): number[] | undefined {
        // Method Implementation: simple
        return this._values;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set values(value: number[] | undefined) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValuesDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: IMultiNumericQuestion);
    public constructor(source: Partial<Pick<MultiNumericQuestion, 'values' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IMultiNumericQuestion | Partial<Pick<MultiNumericQuestion, 'values' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'required' | 'validationState' | 'validationMessages' | 'valueSource' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiNumericQuestionModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiNumericQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('double'));
                
                        // Implementation: axon/write/double
                        writer.writeDoubleValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: number = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: double
                        if (arrayItemHeader.type === 'double') {
                            item = reader.readDoubleValue();
                        }
                        else if (arrayItemHeader.type === 'single') {
                            item = reader.readFloatValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: double
                    writer.writeDoubleValue(item);
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: number;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: double
                        item = reader.readDoubleValue();
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): MultiNumericQuestion {
        // Method Implementation: simple
        const schema = MultiNumericQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiNumericQuestionZodSchemaShape): MultiNumericQuestion {
        // Method Implementation: parseZodSchema
        return new MultiNumericQuestion({
            name: schema.name,
            source: schema.source,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            valueSource: schema.valueSource,
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            values: schema.values?.map(i => i),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'multiNumeric',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): MultiNumericQuestion {
        // Method Implementation: clone
        return new MultiNumericQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export abstract class AAnswer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'enabled', 'visible', 'assignmentState' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AAnswer.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _assignmentState: Core.OptionStates;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get assignmentState(): Core.OptionStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set assignmentState(value: Core.OptionStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<AAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IAnswer | Partial<Pick<AAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._assignmentState = Core.OptionStates.Assignable;
            this._isAssignmentStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AAnswer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'assignmentState':
                return this.assignmentState;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): AAnswer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): AAnswer {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.answerType)
        {
            case 'stringAnswer':
            case 'string':
                return StringAnswer.parse(schema);
            case 'numericAnswer':
            case 'numeric':
                return NumericAnswer.parse(schema);
            default:
                throw new Error(`answerType of ${ schema.answerType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.OptionStates[this.assignmentState]) : undefined,
        };
    }

    public abstract clone(): AAnswer;
}

export class StringAnswer extends AAnswer implements IStringAnswer {
    public static readonly modelName: string = 'StringAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringAnswer.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringAnswer);
    public constructor(source: Partial<Pick<StringAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IStringAnswer | Partial<Pick<StringAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringAnswer {
        // Method Implementation: simple
        const schema = StringAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringAnswerZodSchemaShape): StringAnswer {
        // Method Implementation: parseZodSchema
        return new StringAnswer({
            name: schema.name,
            displayName: schema.displayName,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringAnswer {
        // Method Implementation: clone
        return new StringAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericAnswer extends AAnswer implements INumericAnswer {
    public static readonly modelName: string = 'NumericAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericAnswer.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericAnswer);
    public constructor(source: Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: INumericAnswer | Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericAnswer {
        // Method Implementation: simple
        const schema = NumericAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericAnswerZodSchemaShape): NumericAnswer {
        // Method Implementation: parseZodSchema
        return new NumericAnswer({
            name: schema.name,
            displayName: schema.displayName,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericAnswer {
        // Method Implementation: clone
        return new NumericAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class ValidationMessage implements IValidationMessage {
    public static readonly modelName: string = 'ValidationMessage';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'message', 'type', 'transient' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ValidationMessage.modelName;
    }

    private _message: string;

    private _type: ValidationMessageStates;

    private _transient: boolean;

    public get message(): string {
        // Method Implementation: simple
        return this._message;
    }

    public get type(): ValidationMessageStates {
        // Method Implementation: simple
        return this._type;
    }

    public get transient(): boolean {
        // Method Implementation: simple
        return this._transient;
    }

    public set message(value: string) {
        // Method Implementation: simple
        this._message = value;
        this._isMessageDefined = true;
    }

    public set type(value: ValidationMessageStates) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set transient(value: boolean) {
        // Method Implementation: simple
        this._transient = value;
        this._isTransientDefined = true;
    }

    private _isMessageDefined: boolean;

    private _isTypeDefined: boolean;

    private _isTransientDefined: boolean;

    public get isMessageDefined(): boolean {
        // Method Implementation: simple
        return this._isMessageDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isTransientDefined(): boolean {
        // Method Implementation: simple
        return this._isTransientDefined;
    }

    public constructor();
    public constructor(source: IValidationMessage);
    public constructor(source: Partial<Pick<ValidationMessage, 'message' | 'type' | 'transient'>>);
    public constructor(source?: IValidationMessage | Partial<Pick<ValidationMessage, 'message' | 'type' | 'transient'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValidationMessageModel(source)) {
                if (source.isMessageDefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidationMessageStates.Info;
                    this._isTypeDefined = false;
                }
                
                if (source.isTransientDefined) {
                    this._transient = source.transient;
                    this._isTransientDefined = true;
                }
                else {
                    this._transient = false;
                    this._isTransientDefined = false;
                }
            }
            else {
                if (source.message !== undefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidationMessageStates.Info;
                    this._isTypeDefined = false;
                }
                
                if (source.transient !== undefined) {
                    this._transient = source.transient;
                    this._isTransientDefined = true;
                }
                else {
                    this._transient = false;
                    this._isTransientDefined = false;
                }
            }
        }
        else {
            this._message = '';
            this._isMessageDefined = false;
        
            this._type = ValidationMessageStates.Info;
            this._isTypeDefined = false;
        
            this._transient = false;
            this._isTransientDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ValidationMessage.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isMessageDefined && this.message !== undefined)
            definedProperties.push('message');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isTransientDefined && this.transient !== undefined)
            definedProperties.push('transient');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'message':
                return this.message;
            case 'type':
                return this.type;
            case 'transient':
                return this.transient;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isMessageDefined && this.message !== undefined) {
            if (this.message !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.message);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isTransientDefined && this.transient !== undefined) {
            if (this.transient !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('transient', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.transient);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('transient', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'message':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.message = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'message' cannot be null`);
                    this.message = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'message'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'transient':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.transient = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'transient' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'transient'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // message
        if (this.isMessageDefined && this.message !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.message === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.message);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // transient
        if (this.isTransientDefined && this.transient !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.transient);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // message
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.message = <any>null;
                    break;
                case 1:
                    this.message = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // transient
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.transient = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): ValidationMessage {
        // Method Implementation: simple
        const schema = ValidationMessageZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValidationMessageZodSchemaShape): ValidationMessage {
        // Method Implementation: parseZodSchema
        return new ValidationMessage({
            message: schema.message,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'Info':
                    return ValidationMessageStates.Info;
                case 'SoftInvalid':
                    return ValidationMessageStates.SoftInvalid;
                case 'HardInvalid':
                    return ValidationMessageStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            transient: schema.transient
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            message: this.isMessageDefined ? ((this.message === null ? undefined : this.message)) : undefined,
            type: this.isTypeDefined ? (ValidationMessageStates[this.type]) : undefined,
            transient: this.isTransientDefined ? (this.transient) : undefined,
        };
    }

    public clone(): ValidationMessage {
        // Method Implementation: clone
        return new ValidationMessage({
            message: this.isMessageDefined ? this.message : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            transient: this.isTransientDefined ? this.transient : undefined
        });
    }
}

export class ConflictScenario implements IConflictScenario {
    public static readonly modelName: string = 'ConflictScenario';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'state', 'messages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenario.modelName;
    }

    private _state: ConflictStates;

    private _messages: ValidationMessage[];

    public get state(): ConflictStates {
        // Method Implementation: simple
        return this._state;
    }

    public get messages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._messages;
    }

    public set state(value: ConflictStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    public set messages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._messages = value;
        this._isMessagesDefined = true;
    }

    private _isStateDefined: boolean;

    private _isMessagesDefined: boolean;

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public get isMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isMessagesDefined;
    }

    public constructor();
    public constructor(source: IConflictScenario);
    public constructor(source: Partial<Pick<ConflictScenario, 'state' | 'messages'>>);
    public constructor(source?: IConflictScenario | Partial<Pick<ConflictScenario, 'state' | 'messages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioModel(source)) {
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = ConflictStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.isMessagesDefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
            else {
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = ConflictStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.messages !== undefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
        }
        else {
            this._state = ConflictStates.Valid;
            this._isStateDefined = false;
        
            this._messages = [];
            this._isMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenario.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        if (this.isMessagesDefined && this.messages !== undefined)
            definedProperties.push('messages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'state':
                return this.state;
            case 'messages':
                return this.messages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
        
        if (this.isMessagesDefined && this.messages !== undefined) {
            if (this.messages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.messages.length));
                for (let i = 0; i < this.messages.length; i++) {
                    const item = this.messages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            case 'messages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.messages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'messages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'messages'`);
                        }
                
                        this.messages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'messages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'messages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // messages
        if (this.isMessagesDefined && this.messages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.messages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.messages.length);
            
                for (const item of this.messages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
        
        // messages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.messages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.messages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenario {
        // Method Implementation: simple
        const schema = ConflictScenarioZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioZodSchemaShape): ConflictScenario {
        // Method Implementation: parseZodSchema
        return new ConflictScenario({
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Valid':
                    return ConflictStates.Valid;
                case 'SoftInvalid':
                    return ConflictStates.SoftInvalid;
                case 'HardInvalid':
                    return ConflictStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            messages: schema.messages?.map(i => ValidationMessage.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            state: this.isStateDefined ? (ConflictStates[this.state]) : undefined,
            messages: this.isMessagesDefined ? (this.messages?.map(item => item)) : undefined,
        };
    }

    public clone(): ConflictScenario {
        // Method Implementation: clone
        return new ConflictScenario({
            state: this.isStateDefined ? this.state : undefined,
            messages: this.isMessagesDefined ? this.messages?.map(i => i.clone()) : undefined
        });
    }
}

export class RecommendedValue implements IRecommendedValue {
    public static readonly modelName: string = 'RecommendedValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'displayValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return RecommendedValue.modelName;
    }

    private _source: string;

    private _displayValue: string;

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get displayValue(): string {
        // Method Implementation: simple
        return this._displayValue;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set displayValue(value: string) {
        // Method Implementation: simple
        this._displayValue = value;
        this._isDisplayValueDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isDisplayValueDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayValueDefined;
    }

    public constructor();
    public constructor(source: IRecommendedValue);
    public constructor(source: Partial<Pick<RecommendedValue, 'source' | 'displayValue'>>);
    public constructor(source?: IRecommendedValue | Partial<Pick<RecommendedValue, 'source' | 'displayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isRecommendedValueModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDisplayValueDefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.displayValue !== undefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._displayValue = '';
            this._isDisplayValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return RecommendedValue.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDisplayValueDefined && this.displayValue !== undefined)
            definedProperties.push('displayValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'displayValue':
                return this.displayValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            if (this.displayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'displayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayValue' cannot be null`);
                    this.displayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayValue
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayValue = <any>null;
                    break;
                case 1:
                    this.displayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): RecommendedValue {
        // Method Implementation: simple
        const schema = RecommendedValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: RecommendedValueZodSchemaShape): RecommendedValue {
        // Method Implementation: parseZodSchema
        return new RecommendedValue({
            source: schema.source,
            displayValue: schema.displayValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            displayValue: this.isDisplayValueDefined ? ((this.displayValue === null ? undefined : this.displayValue)) : undefined,
        };
    }

    public clone(): RecommendedValue {
        // Method Implementation: clone
        return new RecommendedValue({
            source: this.isSourceDefined ? this.source : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'LinkedLayout':
                return new LinkedLayout();
            case 'HistoricalState':
                return new HistoricalState();
            case 'Group':
                return new Group();
            case 'StringQuestion':
                return new StringQuestion();
            case 'NumericQuestion':
                return new NumericQuestion();
            case 'BooleanQuestion':
                return new BooleanQuestion();
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion();
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion();
            case 'MultiStringQuestion':
                return new MultiStringQuestion();
            case 'MultiNumericQuestion':
                return new MultiNumericQuestion();
            case 'StringAnswer':
                return new StringAnswer();
            case 'NumericAnswer':
                return new NumericAnswer();
            case 'ValidationMessage':
                return new ValidationMessage();
            case 'ConflictScenario':
                return new ConflictScenario();
            case 'RecommendedValue':
                return new RecommendedValue();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'LinkedLayout':
                return new LinkedLayout(source);
            case 'HistoricalState':
                return new HistoricalState(source);
            case 'Group':
                return new Group(source);
            case 'StringQuestion':
                return new StringQuestion(source);
            case 'NumericQuestion':
                return new NumericQuestion(source);
            case 'BooleanQuestion':
                return new BooleanQuestion(source);
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion(source);
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion(source);
            case 'MultiStringQuestion':
                return new MultiStringQuestion(source);
            case 'MultiNumericQuestion':
                return new MultiNumericQuestion(source);
            case 'StringAnswer':
                return new StringAnswer(source);
            case 'NumericAnswer':
                return new NumericAnswer(source);
            case 'ValidationMessage':
                return new ValidationMessage(source);
            case 'ConflictScenario':
                return new ConflictScenario(source);
            case 'RecommendedValue':
                return new RecommendedValue(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'LinkedLayout':
                return LinkedLayout.parse(source);
            case 'HistoricalState':
                return HistoricalState.parse(source);
            case 'Group':
                return Group.parse(source);
            case 'StringQuestion':
                return StringQuestion.parse(source);
            case 'NumericQuestion':
                return NumericQuestion.parse(source);
            case 'BooleanQuestion':
                return BooleanQuestion.parse(source);
            case 'StringEnumerableQuestion':
                return StringEnumerableQuestion.parse(source);
            case 'NumericEnumerableQuestion':
                return NumericEnumerableQuestion.parse(source);
            case 'MultiStringQuestion':
                return MultiStringQuestion.parse(source);
            case 'MultiNumericQuestion':
                return MultiNumericQuestion.parse(source);
            case 'StringAnswer':
                return StringAnswer.parse(source);
            case 'NumericAnswer':
                return NumericAnswer.parse(source);
            case 'ValidationMessage':
                return ValidationMessage.parse(source);
            case 'ConflictScenario':
                return ConflictScenario.parse(source);
            case 'RecommendedValue':
                return RecommendedValue.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
